import React, { useState, useEffect } from 'react';
import { reportBuilderAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import {
  FileText,
  Download,
  Save,
  Plus,
  Trash2,
  GripVertical,
  RefreshCw,
  BarChart3,
  Users,
  ArrowLeftRight,
  AlertTriangle,
  FileCheck,
  Target,
  Clock,
  Settings,
} from 'lucide-react';
import { toast } from 'sonner';

const SECTION_ICONS = {
  summary: BarChart3,
  entities: Users,
  transactions: ArrowLeftRight,
  alerts: AlertTriangle,
  cases: FileCheck,
  risk_analysis: Target,
};

const ReportBuilder = () => {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState('builder');
  const [dateRange, setDateRange] = useState('30');
  const [reportFormat, setReportFormat] = useState('pdf');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sectionsRes, templatesRes] = await Promise.all([
        reportBuilderAPI.getSections(),
        reportBuilderAPI.listTemplates(),
      ]);
      const sectionsWithEnabled = sectionsRes.data.map(s => ({ ...s, enabled: true }));
      setSections(sectionsWithEnabled);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error('Failed to load report builder data');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    ));
  };

  const moveSection = (index, direction) => {
    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= sections.length) return;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    newSections.forEach((s, i) => s.order = i);
    setSections(newSections);
  };

  const loadTemplate = async (template) => {
    setSelectedTemplate(template);
    setSections(template.sections.map(s => ({ ...s, enabled: s.enabled !== false })));
    setDateRange(String(template.date_range_days || 30));
    toast.success(`Template "${template.name}" loaded`);
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      const templateData = {
        name: templateName,
        description: templateDescription,
        sections: sections.map((s, i) => ({ ...s, order: i })),
        date_range_days: parseInt(dateRange),
        filters: {},
      };

      if (selectedTemplate) {
        await reportBuilderAPI.updateTemplate(selectedTemplate.id, templateData);
        toast.success('Template updated');
      } else {
        await reportBuilderAPI.createTemplate(templateData);
        toast.success('Template saved');
      }
      
      setShowSaveDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    try {
      const response = await reportBuilderAPI.generate({
        sections: sections.filter(s => s.enabled).map((s, i) => ({ ...s, order: i })),
        date_range_days: parseInt(dateRange),
        format: reportFormat,
      });
      
      // Download the file
      const blob = new Blob([response.data], { 
        type: reportFormat === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AEGIS_Report_${new Date().toISOString().slice(0, 10)}.${reportFormat}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await reportBuilderAPI.deleteTemplate(templateId);
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="report-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Report Builder</h1>
          <p className="text-gray-400 text-sm mt-1">Create custom reports with drag-and-drop sections</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => setShowSaveDialog(true)} className="gap-2">
            <Save className="w-4 h-4" />
            Save Template
          </Button>
          <Button 
            onClick={generateReport} 
            disabled={generating || !sections.some(s => s.enabled)}
            className="gap-2"
          >
            {generating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Generate Report
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-900/50 border border-gray-800">
          <TabsTrigger value="builder">Report Builder</TabsTrigger>
          <TabsTrigger value="templates">Saved Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Sections List */}
            <div className="lg:col-span-2">
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Report Sections
                  </CardTitle>
                  <CardDescription>Select and order the sections for your report</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {sections.map((section, index) => {
                    const Icon = SECTION_ICONS[section.type] || FileText;
                    return (
                      <div
                        key={section.id}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-all ${
                          section.enabled 
                            ? 'bg-gray-800/50 border-cyan-500/30' 
                            : 'bg-gray-900/30 border-gray-700 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => moveSection(index, 'up')}
                              disabled={index === 0}
                            >
                              ▲
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 w-5 p-0"
                              onClick={() => moveSection(index, 'down')}
                              disabled={index === sections.length - 1}
                            >
                              ▼
                            </Button>
                          </div>
                          <GripVertical className="w-4 h-4 text-gray-500" />
                          <Checkbox
                            checked={section.enabled}
                            onCheckedChange={() => toggleSection(section.id)}
                          />
                          <Icon className={`w-5 h-5 ${section.enabled ? 'text-cyan-400' : 'text-gray-500'}`} />
                          <div>
                            <p className="text-white font-medium">{section.title}</p>
                            <p className="text-gray-400 text-xs capitalize">{section.type.replace('_', ' ')}</p>
                          </div>
                        </div>
                        <Badge variant={section.enabled ? 'default' : 'outline'} className={section.enabled ? 'bg-cyan-500/20 text-cyan-400' : ''}>
                          {section.enabled ? 'Included' : 'Excluded'}
                        </Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>

            {/* Settings */}
            <div className="space-y-4">
              <Card className="bg-gray-900/50 border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Report Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-gray-400">Date Range</Label>
                    <Select value={dateRange} onValueChange={setDateRange}>
                      <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="7">Last 7 days</SelectItem>
                        <SelectItem value="14">Last 14 days</SelectItem>
                        <SelectItem value="30">Last 30 days</SelectItem>
                        <SelectItem value="60">Last 60 days</SelectItem>
                        <SelectItem value="90">Last 90 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-gray-400">Export Format</Label>
                    <Select value={reportFormat} onValueChange={setReportFormat}>
                      <SelectTrigger className="mt-1 bg-gray-800 border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-900 border-gray-700">
                        <SelectItem value="pdf">PDF Document</SelectItem>
                        <SelectItem value="excel">Excel Spreadsheet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-4 border-t border-gray-700">
                    <p className="text-sm text-gray-400 mb-2">Selected Sections</p>
                    <p className="text-2xl font-bold text-cyan-400">
                      {sections.filter(s => s.enabled).length} / {sections.length}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {selectedTemplate && (
                <Card className="bg-cyan-500/10 border-cyan-500/30">
                  <CardContent className="p-4">
                    <p className="text-cyan-400 text-sm font-medium">Active Template</p>
                    <p className="text-white font-bold">{selectedTemplate.name}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-2 text-xs"
                      onClick={() => {
                        setSelectedTemplate(null);
                        fetchData();
                      }}
                    >
                      Clear Template
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardHeader>
              <CardTitle className="text-white text-lg">Saved Templates</CardTitle>
              <CardDescription>Load a saved template or create a new one</CardDescription>
            </CardHeader>
            <CardContent>
              {templates.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {templates.map((template) => (
                    <Card key={template.id} className="bg-gray-800/50 border-gray-700">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="text-white font-medium">{template.name}</h3>
                            <p className="text-gray-400 text-xs mt-1">{template.description || 'No description'}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-red-400"
                            onClick={() => deleteTemplate(template.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                          <Clock className="w-3 h-3" />
                          {template.date_range_days} days • {template.sections?.length || 0} sections
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-3"
                          onClick={() => {
                            loadTemplate(template);
                            setActiveTab('builder');
                          }}
                        >
                          Load Template
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No saved templates yet</p>
                  <p className="text-gray-500 text-sm mt-1">Create one by configuring sections and clicking Save Template</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="bg-gray-900 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Save Report Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-400">Template Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Monthly Risk Report"
                className="mt-1 bg-gray-800 border-gray-700"
              />
            </div>
            <div>
              <Label className="text-gray-400">Description (Optional)</Label>
              <Input
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                placeholder="Brief description of this template"
                className="mt-1 bg-gray-800 border-gray-700"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveDialog(false)}>Cancel</Button>
            <Button onClick={saveTemplate}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportBuilder;
